/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */

import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import styled from "styled-components";
import { datadogRum } from "@datadog/browser-rum";
import { format } from "date-fns";

import * as API from "../../../../api/billing";
import Button from "../../../reusable/button";
import DatePicker from "../../../reusable/datePicker";
import { CSVExportModalBodyProps } from "../lib/types";
import {
  StyledButtonContainer,
  StyledModalBody,
  StyledModalContents,
} from "../lib/styles";
import { downloadFile } from "../../../../utils";
import { redesignTheme } from "../../../theme/theme";

const StyledSpan = styled.span`
  padding-right: ${redesignTheme.spacing[4.5]};
  padding-left: ${redesignTheme.spacing[4.5]};
  margin-bottom: ${redesignTheme.spacing[2.5]};
  vertical-align: bottom;
  display: inline-block;
`;

const StyledContainer = styled(StyledButtonContainer)`
  justify-content: flex-end;
`;

const StyledDateWrapper = styled.div`
  display: inline-block;
  width: 44%;
`;

const StyledButton = styled(Button)`
  float: right;
  margin-top: 0;
  margin-right: 0;
`;

const CSVExportModalBody = ({
  closeModal,
  updateToastMessage,
}: CSVExportModalBodyProps): React.ReactElement => {
  const [loading, setLoading] = React.useState(false);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);

  const today = new Date();

  const handleSuccessfulGeneratedFileResponse = (data: {
    id: string;
    file_url: string;
  }): void => {
    API.setGeneratedFileDownloadedAt({ id: data.id }).then(() => {
      document.location = data.file_url;
    });
  };

  const handleSuccessfulCsvResponse = (data: { id: string }): void => {
    setLoading(false);
    closeModal();
    updateToastMessage({
      isOpen: true,
      message: i18n.translate(
        "admin_pages.activity.report_generation_in_progress"
      ),
      type: "success",
    });

    const interval = setInterval(async () => {
      const response = await API.getGeneratedFile({ id: data.id });
      if (response && response.data) {
        const generatedFileData = response.data;
        if (generatedFileData.generated) {
          clearInterval(interval);
          handleSuccessfulGeneratedFileResponse({
            id: data.id,
            file_url: generatedFileData.file_url,
          });
        }
      }
    }, 5000);
  };

  const handleExportClick = async (): Promise<void> => {
    setLoading(true);

    try {
      const previousDownloadResponse = await API.getInProgressGeneratedFile();
      if (
        previousDownloadResponse &&
        previousDownloadResponse.data &&
        previousDownloadResponse.data.length > 0
      ) {
        setLoading(false);
        closeModal();
        updateToastMessage({
          isOpen: true,
          message: i18n.translate(
            "admin_pages.activity.another_report_is_generating"
          ),
          type: "warning",
        });
        return;
      }

      const formattedStartDate = format(startDate, "yyyy-MM-dd");
      const formattedEndDate = format(endDate, "yyyy-MM-dd");
      const response = await API.getRewardsCSVDowload({
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      });

      if (response && response.data) {
        handleSuccessfulCsvResponse(response.data);
      } else {
        throw new Error("Unable to get rewards CSV for selected date range");
      }
    } catch (error) {
      datadogRum.addError(error);
      setLoading(false);
      updateToastMessage({
        isOpen: true,
        message: i18n.translate("admin_pages.activity.download_csv_failure"),
        type: "error",
      });
    }
  };

  return (
    <StyledModalBody data-testid="csv-export-modal-body">
      <StyledModalContents>
        <p>{i18n.translate("admin_pages.activity.export_csv_modal.text")}</p>

        <StyledDateWrapper>
          <DatePicker
            handleChange={setStartDate}
            id="start-date"
            label={i18n.translate(
              "admin_pages.activity.export_csv_modal.start_date"
            )}
            maxDate={endDate || today}
            maxDateMessage={i18n.translate(
              "admin_pages.activity.export_csv_modal.start_date_message"
            )}
            value={startDate}
          />
        </StyledDateWrapper>

        <StyledSpan>
          {i18n.translate("admin_pages.activity.export_csv_modal.to")}
        </StyledSpan>

        <StyledDateWrapper>
          <DatePicker
            handleChange={setEndDate}
            id="end-date"
            label={i18n.translate(
              "admin_pages.activity.export_csv_modal.end_date"
            )}
            minDate={startDate}
            minDateMessage={i18n.translate(
              "admin_pages.activity.export_csv_modal.end_date_min_message"
            )}
            maxDate={today}
            maxDateMessage={i18n.translate(
              "admin_pages.activity.export_csv_modal.end_date_max_message"
            )}
            value={endDate}
          />
        </StyledDateWrapper>
      </StyledModalContents>
      <StyledContainer>
        <StyledButton
          disabled={!startDate || !endDate || loading}
          onClick={handleExportClick}
          size="small"
          testId="export-to-csv-button"
          variant="primary"
        >
          {i18n.translate(
            "admin_pages.activity.export_csv_modal.export_to_csv"
          )}
        </StyledButton>
      </StyledContainer>
    </StyledModalBody>
  );
};

export default CSVExportModalBody;
